import type { ComboKeyParts, NumericString, Ref, SectionRow } from './types';

/* Numeric, currency and combination-matching primitives shared by the whole calculation engine.
   Ported from server/services/development-orders/product-costing-calcaution-service.js (L33-230, L525-589),
   whose behaviour matches the detail page's checkIsNanAndNumber / convertedCurrValue / roundOff. */

/** Parses any stored costing value to a finite number, collapsing null/NaN/'' to 0. */
export function toFiniteNumber(value: unknown): number {
  const parsed = parseFloat(String(value ?? ''));
  return Number.isFinite(parsed) ? parsed : 0;
}

/** Rounds to a fixed number of decimals, matching lodash `_.round` used by the backend engine. */
export function roundTo(value: number, decimals = 2): number {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** decimals;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

/**
 * Converts a supplier-currency figure into the company currency.
 * Mirrors Components/helper.js convertedCurrValue: the rates are only compared for equality, and a
 * mismatch divides by the primary rate.
 */
export function convertedCurrValue(value: number, primaryCurrencyValue: NumericString, secondaryCurrencyValue: NumericString): number {
  const primaryRate = primaryCurrencyValue ? Number(primaryCurrencyValue) : 1;
  const secondaryRate = secondaryCurrencyValue ? Number(secondaryCurrencyValue) : 1;
  if (primaryRate !== secondaryRate) return value / primaryRate;
  return value;
}

/** Normalises any id-bearing value (populated ref, ObjectId, string) to a string, or null. */
export function toIdString(value: Ref | string | null | undefined): string | null {
  const raw = typeof value === 'object' && value !== null ? value._id : value;
  if (raw == null || raw === '') return null;
  return typeof raw === 'string' ? raw : String(raw);
}

/** Resolves a row's colourway id, tolerating both `colourway` and the legacy `colourWay` spelling. */
export function resolveColourwayId(row: { colourway?: Ref | string | null; colourWay?: Ref | string | null } | undefined): string {
  return toIdString(row?.colourway ?? row?.colourWay) ?? 'null';
}

/** Resolves a row's length id. */
export function resolveLengthId(row: { length?: Ref | string | null } | undefined): string {
  return toIdString(row?.length) ?? 'null';
}

/** Resolves a row's size, collapsing blank values to the literal 'null' used by the combination key. */
export function resolveSize(row: { size?: string | null } | undefined): string {
  const sizeValue = row?.size;
  return sizeValue == null || String(sizeValue).trim() === '' ? 'null' : String(sizeValue).trim();
}

/** Builds the `colourway_size_length` key used to group BOM and grand-total rows. */
export function getComboKey(row: Parameters<typeof resolveColourwayId>[0] & Parameters<typeof resolveLengthId>[0] & Parameters<typeof resolveSize>[0]): string {
  return `${resolveColourwayId(row)}_${resolveSize(row)}_${resolveLengthId(row)}`;
}

/**
 * Builds the toggle-aware combination descriptor used to match a landed row against a grand-total row.
 * Only the dimensions the costing actually varies by participate, so a costing without colourways
 * matches on size/length alone. Ported from the backend buildComboObjectForMatching (L525).
 */
export function buildComboObjectForMatching(
  row: { colourway?: Ref | string | null; colourWay?: Ref | string | null; size?: string | null; length?: Ref | string | null } | undefined,
  colourWayToggle: boolean,
  sizeLengthToggle: boolean,
  hasProductLengths: boolean,
): ComboKeyParts {
  let colourWayId: string | null = null;
  let size = '';
  let lengthId: string | null = null;

  if (colourWayToggle) {
    colourWayId = toIdString(row?.colourway ?? row?.colourWay);
  }
  if (sizeLengthToggle) {
    const rawSize = row?.size;
    size = rawSize == null || String(rawSize).trim() === '' ? '' : String(rawSize).trim();
    if (hasProductLengths) lengthId = toIdString(row?.length);
  }

  return { colourWayId, size, lengthId };
}

/** True when two combination descriptors identify the same row. */
export function isSameCombo(left: ComboKeyParts, right: ComboKeyParts): boolean {
  return left.colourWayId === right.colourWayId && left.size === right.size && left.lengthId === right.lengthId;
}

/**
 * Sums a testing / setup-cost / additional-component section.
 * Returns a per-colourway map when the costing varies by colourway, otherwise a single total —
 * the same dual shape the detail page's sumPricePerUnitTestingAdditionalSection returns.
 */
export function sumSectionValues(rows: SectionRow[] | undefined, colourWayToggle: boolean, key: 'pricePerUnit' | 'totalPrice' = 'pricePerUnit'): number | Record<string, number> {
  if (!Array.isArray(rows)) return colourWayToggle ? {} : 0;
  const validRows = rows.filter((row) => !row?.isExcluded && toFiniteNumber(row?.[key]) > 0);

  if (colourWayToggle) {
    const groupedSums: Record<string, number> = {};
    validRows.forEach((row) => {
      const colourwayId = toIdString(row?.colourway ?? row?.colourWay);
      if (!colourwayId) return;
      groupedSums[colourwayId] = (groupedSums[colourwayId] ?? 0) + toFiniteNumber(row[key]);
    });
    return groupedSums;
  }

  return validRows.reduce((sum, row) => sum + toFiniteNumber(row[key]), 0);
}

/** Reads one colourway's share out of a section sum, or the flat total when not colourway-split. */
export function pickSectionSum(sumData: number | Record<string, number> | undefined, colourwayId: string): number {
  if (colourwayId !== 'null' && sumData && typeof sumData === 'object') {
    return toFiniteNumber(sumData[colourwayId]);
  }
  return toFiniteNumber(sumData);
}
